import type { ReactNode } from "react";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

import { Button } from "@alpacahq/ui/old";

import { Icon } from "~/v2/components/AssetIcon/AssetIcon";

interface AddressMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirmLoading: boolean;
  chainLabel: string;
  chainCode: string;
  assetLabel: string;
  assetCode: string;
  address: string;
  sendingTo: string;
  isSelfHosted: boolean;
  ownsWallet: boolean | null;
  walletOwnerName: string | null;
  vaspName: string;
  vaspDid: string;
}

const yesNo = (value: boolean): string => (value ? "Yes" : "No");

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-center justify-between gap-4 py-1">
    <span className="text-gray-700 text-sm">{label}</span>
    <div className="min-w-0 text-right font-medium text-gray-950 text-sm">
      {children}
    </div>
  </div>
);

export const AddressMatchModal = ({
  isOpen,
  onClose,
  onConfirm,
  isConfirmLoading,
  chainLabel,
  chainCode,
  assetLabel,
  assetCode,
  address,
  sendingTo,
  isSelfHosted,
  ownsWallet,
  walletOwnerName,
  vaspName,
  vaspDid,
}: AddressMatchModalProps) => (
  <Dialog
    open={isOpen}
    onClose={onClose}
    className="relative z-50 overflow-y-auto"
  >
    <DialogBackdrop
      transition
      className="fixed inset-0 bg-black/30 duration-300 ease-out data-closed:opacity-0"
    />
    <div className="fixed inset-0 w-screen overflow-y-auto p-4">
      <div className="flex min-h-full items-center justify-center">
        <DialogPanel
          transition
          className="flex w-full max-w-[600px] flex-col gap-4 rounded border border-gray-200 bg-white p-4 duration-300 ease-out data-closed:scale-95 data-closed:opacity-0"
        >
          <div className="flex flex-col gap-2">
            <DialogTitle className="m-0 p-0 text-left font-semibold text-gray-950 text-lg">
              We found a match for this address
            </DialogTitle>
            <p className="m-0 p-0 text-gray-700 text-sm">
              Our records indicate this address belongs to a verified Virtual
              Asset Service Provider (VASP). We&apos;ve updated the details
              below to match the verified source. Would you like to proceed with
              this information?
            </p>
          </div>

          <div className="flex flex-col rounded bg-gray-50 p-2">
            <Row label="Chain">
              <span className="inline-flex items-center justify-end gap-2">
                <Icon name={chainCode} className="h-5 w-5" />
                <span>{chainLabel}</span>
              </span>
            </Row>
            <Row label="Asset">
              <span className="inline-flex items-center justify-end gap-2">
                <Icon name={assetCode} className="h-5 w-5" />
                <span>{assetLabel}</span>
              </span>
            </Row>
            <Row label="Address">
              <span className="break-all font-mono text-xs">{address}</span>
            </Row>
            <Row label="Sending to">{sendingTo}</Row>
            <Row label="Self hosted wallet">{yesNo(isSelfHosted)}</Row>
            {ownsWallet !== null && (
              <Row label="Do you own the wallet">{yesNo(ownsWallet)}</Row>
            )}
            {walletOwnerName && (
              <Row label="Wallet Owner">{walletOwnerName}</Row>
            )}
            <Row label="Verified VASP">
              <div className="flex flex-col items-end">
                <span>{vaspName}</span>
                {vaspDid && (
                  <span className="font-mono text-gray-500 text-xs">
                    {vaspDid}
                  </span>
                )}
              </div>
            </Row>
          </div>

          <div className="flex items-center justify-end gap-4">
            <Button
              className="border-gray-400"
              variant="v2/stroke"
              onClick={onClose}
              disabled={isConfirmLoading}
            >
              Cancel
            </Button>
            <Button
              variant="v2/filled"
              onClick={onConfirm}
              loading={isConfirmLoading}
            >
              Accept & Add Address
            </Button>
          </div>
        </DialogPanel>
      </div>
    </div>
  </Dialog>
);
